import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  MapPin, QrCode, Plus, Route as RouteIcon, ScanLine, Play, MoreVertical,
  Printer, Download, Trash2, Footprints, Building2, CalendarClock, Moon,
} from "lucide-react";
import { AddressSearch } from "@/components/GeofenceWizard";
import {
  OnSiteScheduleFields, type OnSiteScheduleValue,
} from "@/components/guard-tours/OnSiteScheduleFields";
import {
  describeSchedule, isOvernight, enforcementLabel,
} from "@/lib/tour-schedule";
import {
  fetchMyShiftWindows, tourAvailability, type MyShift,
} from "@/lib/tour-shift-link";
import {
  exportPdf, openPrintSheet, type CheckpointForExport,
} from "@/lib/qr-export";


export const Route = createFileRoute("/_authenticated/guard-tours/")({
  component: GuardToursPage,
});

type TourType = "patrol" | "on_site";

type Tour = {
  id: string;
  name: string;
  description: string | null;
  expected_duration_min: number | null;
  active: boolean;
  site_id: string | null;
  organization_id: string;
  created_at: string;
  tour_type: TourType;
  schedule_days: number[] | null;
  schedule_start_time: string | null;
  schedule_end_time: string | null;
  post_order_id: string | null;
  schedule_enforcement: string | null;
  advisory_note: string | null;
  checkpoint_count?: number;
};

type Site = { id: string; name: string; address: string | null };
type PostOrderOption = { id: string; title: string; site_id: string };


function GuardToursPage() {
  const { currentOrg, user, currentRole } = useAuth();
  const navigate = useNavigate();
  const [tours, setTours] = useState<Tour[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [postOrders, setPostOrders] = useState<PostOrderOption[]>([]);
  const [myShifts, setMyShifts] = useState<MyShift[]>([]);
  const [typeFilter, setTypeFilter] = useState<"all" | TourType>("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [siteMode, setSiteMode] = useState<"existing" | "new">("existing");
  const [siteId, setSiteId] = useState<string>("");
  const [newSiteName, setNewSiteName] = useState("");
  const [newSiteCenter, setNewSiteCenter] = useState<{ lat: number; lng: number; label: string } | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState<number>(30);
  const [count, setCount] = useState<number>(5);
  const [tourType, setTourType] = useState<TourType>("patrol");
  const [schedule, setSchedule] = useState<OnSiteScheduleValue>({
    days: [1, 2, 3, 4, 5], startTime: "18:00", endTime: "06:00",
    enforcement: "strict", advisoryNote: "",
  });
  const [postOrderId, setPostOrderId] = useState<string>("none");


  // Solo org_owners (1099 / single user) and admins/supervisors and platform admins all manage.
  const canManage = ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");

  const load = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const [tRes, sRes, poRes] = await Promise.all([
      supabase
        .from("guard_tours")
        .select("id,name,description,expected_duration_min,active,site_id,organization_id,created_at,tour_type,schedule_days,schedule_start_time,schedule_end_time,schedule_enforcement,advisory_note,post_order_id,tour_checkpoints(count)")
        .eq("organization_id", currentOrg.id)
        .order("created_at", { ascending: false }),
      supabase
        .from("sites")
        .select("id,name,address")
        .eq("organization_id", currentOrg.id)
        .eq("active", true)
        .order("name"),
      supabase
        .from("post_orders")
        .select("id,title,site_id")
        .eq("organization_id", currentOrg.id)
        .eq("active", true)
        .order("title"),
    ]);
    setTours(
      (tRes.data ?? []).map((row: any) => ({
        ...row,
        checkpoint_count: row.tour_checkpoints?.[0]?.count ?? 0,
      })),
    );
    setSites((sRes.data ?? []) as Site[]);
    setPostOrders((poRes.data ?? []) as PostOrderOption[]);
    setMyShifts(await fetchMyShiftWindows(currentOrg.id));
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const resetForm = () => {
    setSiteMode(sites.length ? "existing" : "new");
    setSiteId(sites[0]?.id ?? "");
    setNewSiteName(""); setNewSiteCenter(null);
    setName(""); setDescription(""); setDuration(30); setCount(5);
    setSchedule({ days: [1, 2, 3, 4, 5], startTime: "18:00", endTime: "06:00", enforcement: "strict", advisoryNote: "" });
    setPostOrderId("none");
  };

  useEffect(() => {
    if (open) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !user) return;
    if (!name.trim()) return toast.error("Give the tour a name");
    if (tourType === "patrol" && (duration < 0 || Number.isNaN(duration))) {
      return toast.error("Expected duration must be 0 or more minutes");
    }
    if (tourType === "on_site") {
      if (!schedule.days.length) return toast.error("Pick at least one day of the week");
      if (!schedule.startTime || !schedule.endTime) return toast.error("Set the shift start and end time");

    }

    setSubmitting(true);
    try {
      let resolvedSiteId: string | null = null;
      if (siteMode === "existing") {
        if (!siteId) {
          toast.error("Pick a site or create a new one");
          setSubmitting(false);
          return;
        }
        resolvedSiteId = siteId;
      } else {
        if (!newSiteName.trim() || !newSiteCenter) {
          toast.error("Enter a site name and search the address");
          setSubmitting(false);
          return;
        }
        const { data: site, error: siteErr } = await supabase
          .from("sites")
          .insert({
            organization_id: currentOrg.id,
            name: newSiteName.trim(),
            address: newSiteCenter.label,
            latitude: newSiteCenter.lat,
            longitude: newSiteCenter.lng,
            active: true,
          })
          .select("id")
          .single();
        if (siteErr || !site) throw siteErr ?? new Error("Could not create site");
        resolvedSiteId = site.id;
      }

      const { data: tour, error } = await supabase
        .from("guard_tours")
        .insert({
          organization_id: currentOrg.id,
          site_id: resolvedSiteId,
          name: name.trim(),
          description: description.trim() || null,
          tour_type: tourType,
          expected_duration_min: tourType === "on_site" ? null : Math.max(0, duration),
          schedule_days: tourType === "on_site" ? [...schedule.days].sort() : null,
          schedule_start_time: tourType === "on_site" ? schedule.startTime : null,
          schedule_end_time: tourType === "on_site" ? schedule.endTime : null,
          schedule_enforcement: tourType === "on_site" ? schedule.enforcement : "strict",
          advisory_note: tourType === "on_site" ? (schedule.advisoryNote.trim() || null) : null,

          post_order_id: tourType === "on_site" && postOrderId !== "none" ? postOrderId : null,
          allow_repeat_scans: true,
          enforce_full_coverage: false,
          patrol_mode: "flexible",
          created_by: user.id,
        })
        .select()
        .single();
      if (error || !tour) throw error ?? new Error("Could not create tour");

      const cpCount = Math.max(1, Math.min(50, count));
      const checkpoints = Array.from({ length: cpCount }, (_, i) => ({
        tour_id: tour.id,
        organization_id: currentOrg.id,
        sequence: i + 1,
        name: `Checkpoint ${i + 1}`,
      }));
      const { error: cpErr } = await supabase.from("tour_checkpoints").insert(checkpoints);
      if (cpErr) throw cpErr;

      toast.success(
        `${tourType === "on_site" ? "On-site guard tour" : "Patrol tour"} saved successfully · ${cpCount} QR codes generated`,
      );
      setOpen(false);
      await load();
      navigate({ to: "/guard-tours/$tourId", params: { tourId: tour.id } });
    } catch (err: any) {
      toast.error(err?.message ?? "Could not create tour");
    } finally {
      setSubmitting(false);
    }
  };

  const visibleTours = useMemo(
    () => (typeFilter === "all" ? tours : tours.filter((t) => (t.tour_type ?? "patrol") === typeFilter)),
    [tours, typeFilter],
  );

  const groups = useMemo(() => {
    const bySite = new Map<string, Tour[]>();
    for (const t of visibleTours) {
      const key = t.site_id ?? "__unassigned__";
      if (!bySite.has(key)) bySite.set(key, []);
      bySite.get(key)!.push(t);
    }
    return Array.from(bySite.entries()).map(([key, list]) => ({
      site: sites.find((s) => s.id === key) ?? null,
      siteId: key === "__unassigned__" ? null : key,
      tours: list,
    }));
  }, [visibleTours, sites]);


  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
            <Footprints className="h-7 w-7 text-primary" />
            Guard Tours
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Create patrol tours per site, generate QR checkpoints, scan in any order.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/guard-tours/active"><RouteIcon className="h-4 w-4" /> Active patrol</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/guard-tours/scan"><ScanLine className="h-4 w-4" /> Scan QR</Link>
          </Button>
          {canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button><Plus className="h-4 w-4" /> New tour</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => { setTourType("patrol"); setOpen(true); }}>
                    <RouteIcon className="h-4 w-4" /> Patrol Tour
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setTourType("on_site"); setOpen(true); }}>
                    <Building2 className="h-4 w-4" /> On-Site Guard Tour
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <form onSubmit={handleCreate} className="space-y-4">
                  <DialogHeader>
                    <DialogTitle>
                      {tourType === "on_site" ? "New on-site guard tour" : "New patrol tour"}
                    </DialogTitle>
                    <DialogDescription>
                      {tourType === "on_site"
                        ? "A fixed post at one site: set the shift days and hours, link a post order, and we'll generate the QR checkpoints."
                        : "Pick a site (or add a new one), then we'll generate the QR checkpoints for you."}
                    </DialogDescription>
                  </DialogHeader>


                  {/* Site picker */}
                  <div className="space-y-2">
                    <Label>Site</Label>
                    <div className="flex gap-2">
                      <Button type="button" size="sm"
                        variant={siteMode === "existing" ? "default" : "outline"}
                        onClick={() => setSiteMode("existing")}
                        disabled={!sites.length}>
                        Existing site
                      </Button>
                      <Button type="button" size="sm"
                        variant={siteMode === "new" ? "default" : "outline"}
                        onClick={() => setSiteMode("new")}>
                        Add new site
                      </Button>
                    </div>

                    {siteMode === "existing" ? (
                      <Select value={siteId} onValueChange={setSiteId}>
                        <SelectTrigger aria-label="Choose a site"><SelectValue placeholder="Choose a site" /></SelectTrigger>
                        <SelectContent>
                          {sites.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}{s.address ? ` — ${s.address}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="space-y-2 rounded-md border border-border/60 p-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="site-name">Site name</Label>
                          <Input id="site-name" required={siteMode === "new"}
                            value={newSiteName}
                            onChange={(e) => setNewSiteName(e.target.value)}
                            placeholder="e.g. Warehouse 12" />
                        </div>
                        <AddressSearch
                          onPick={(lat, lng, label) => setNewSiteCenter({ lat, lng, label })}
                        />
                        {newSiteCenter && (
                          <p className="text-xs text-muted-foreground">
                            <MapPin className="inline h-3 w-3" /> {newSiteCenter.label}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Tour name</Label>
                    <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                      placeholder={tourType === "on_site" ? "e.g. Front Desk Night Post" : "e.g. Exterior Mobile Patrol"} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="desc">Description (optional)</Label>
                    <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
                  </div>

                  {tourType === "on_site" && (
                    <>
                      <OnSiteScheduleFields
                        value={schedule}
                        onChange={setSchedule}
                        idPrefix="new-onsite"
                      />
                      <div className="space-y-1.5">
                        <Label>Post order (optional)</Label>
                        <Select value={postOrderId} onValueChange={setPostOrderId}>
                          <SelectTrigger aria-label="Post order (optional)" className="w-full min-w-0"><SelectValue placeholder="No post order" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No post order</SelectItem>
                            {postOrders
                              .filter((po) => siteMode === "new" || !siteId || po.site_id === siteId)
                              .map((po) => (
                                <SelectItem key={po.id} value={po.id}>{po.title}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}


                  <div className="grid grid-cols-2 gap-3">
                    {tourType === "patrol" && (
                      <div className="space-y-2">
                        <Label htmlFor="dur">Expected duration (min)</Label>
                        <Input id="dur" type="number" min={0} value={duration}
                          onChange={(e) => {
                            const v = parseInt(e.target.value);
                            setDuration(Number.isNaN(v) ? 0 : Math.max(0, v));
                          }} />
                        <p className="text-xs text-muted-foreground">Use 0 for no time expectation.</p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="cnt"># QR checkpoints</Label>
                      <Input id="cnt" type="number" min={1} max={50} value={count}
                        onChange={(e) => setCount(parseInt(e.target.value) || 1)} />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="submit" disabled={submitting}>
                      {submitting ? "Creating…" : "Create tour & generate QR codes"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {tours.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {([
            ["all", "All tours", tours.length],
            ["patrol", "Patrol tours", tours.filter((t) => (t.tour_type ?? "patrol") === "patrol").length],
            ["on_site", "On-site tours", tours.filter((t) => t.tour_type === "on_site").length],
          ] as const).map(([key, label, n]) => (
            <Button
              key={key} size="sm" variant={typeFilter === key ? "default" : "outline"}
              aria-pressed={typeFilter === key}
              onClick={() => setTypeFilter(key)}
            >
              {label} ({n})
            </Button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : tours.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <RouteIcon className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-medium">No guard tours yet</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Click <span className="font-medium">New tour</span> to add a site and generate QR checkpoints.
          </p>
        </Card>
      ) : visibleTours.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <RouteIcon className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-medium">No tours of this type yet</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Switch back to <span className="font-medium">All tours</span>, or create one from the New tour menu.
          </p>
        </Card>
      ) : (

        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.siteId ?? "unassigned"}>
              <div className="mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {g.site ? g.site.name : "Unassigned"}
                </h2>
                {g.site?.address && (
                  <span className="text-xs text-muted-foreground">· {g.site.address}</span>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {g.tours.map((t) => (
                  <TourCard
                    key={t.id}
                    tour={t}
                    canManage={canManage}
                    myShifts={myShifts}
                    onChanged={load}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function TourCard({
  tour, canManage, myShifts, onChanged,
}: { tour: Tour; canManage: boolean; myShifts: MyShift[]; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const availability = tourAvailability(tour, myShifts);
  // Managers keep full access; guards start on-site tours during their shift.
  const canStart = canManage || availability.available;

  const fetchExportSet = async (): Promise<CheckpointForExport[]> => {
    const { data, error } = await supabase.rpc("get_tour_qr_tokens", { _tour_id: tour.id });
    if (error) throw error;
    return (data ?? []).map((cp: { checkpoint_id: string; name: string; sequence: number; checkpoint_code: string | null; qr_token: string }) => ({
      id: cp.checkpoint_id,
      organization_id: tour.organization_id,
      tour_id: tour.id,
      tour_name: tour.name,
      name: cp.name,
      sequence: cp.sequence,
      checkpoint_code: cp.checkpoint_code,
      qr_token: cp.qr_token,
    }));
  };

  const wrap = async (fn: () => Promise<void>, ok: string) => {
    setBusy(true);
    try { await fn(); toast.success(ok); }
    catch (e: any) { toast.error(e?.message ?? "Action failed"); }
    finally { setBusy(false); }
  };

  const onPrint = () => wrap(async () => {
    const set = await fetchExportSet();
    if (!set.length) throw new Error("No checkpoints to print");
    openPrintSheet(set, undefined, "4up");
  }, "Print sheet opened");

  const onPdf = () => wrap(async () => {
    const set = await fetchExportSet();
    if (!set.length) throw new Error("No checkpoints to export");
    await exportPdf(set, { layout: "4up", includeCode: true });
  }, "PDF downloaded");

  const onDelete = () => wrap(async () => {
    const { error } = await supabase.from("guard_tours").delete().eq("id", tour.id);
    if (error) throw error;
    onChanged();
  }, "Tour deleted");

  return (
    <Card className="flex h-full flex-col p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold">{tour.name}</h3>
          {tour.description && (
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{tour.description}</p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" disabled={busy} aria-label={`Actions for ${tour.name}`}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onPrint}>
              <Printer className="h-4 w-4" /> Print QR sheet
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onPdf}>
              <Download className="h-4 w-4" /> Export PDF
            </DropdownMenuItem>
            {canManage && (
              <>
                <DropdownMenuSeparator />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      className="text-destructive focus:text-destructive">
                      <Trash2 className="h-4 w-4" /> Delete tour
                    </DropdownMenuItem>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this tour?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Removes the tour, its checkpoints and any unscanned QR codes. Past scan history is preserved.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {tour.tour_type === "on_site" ? (
          <Badge className="gap-1 bg-primary/15 text-primary hover:bg-primary/15">
            <Building2 className="h-3 w-3" /> On-site
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1">
            <RouteIcon className="h-3 w-3" /> Patrol
          </Badge>
        )}
        <Badge variant="outline" className="gap-1">
          <QrCode className="h-3 w-3" /> {tour.checkpoint_count ?? 0} checkpoints
        </Badge>
        {tour.tour_type === "on_site" ? (
          <>
            <span className="flex min-w-0 items-center gap-1">
              <CalendarClock className="h-3 w-3 shrink-0" />
              <span className="break-words">
                {describeSchedule(tour.schedule_days, tour.schedule_start_time, tour.schedule_end_time)}
              </span>
              {isOvernight(tour.schedule_start_time, tour.schedule_end_time) && (
                <Moon className="h-3 w-3 shrink-0" />
              )}
            </span>
            <Badge
              variant={availability.available ? "default" : "outline"}
              className="shrink-0"
              title={availability.detail}
            >
              {availability.label}
            </Badge>
            <Badge variant="secondary" className="shrink-0">
              {enforcementLabel(tour.schedule_enforcement === "flexible" ? "flexible" : "strict")}
            </Badge>
          </>
        ) : tour.expected_duration_min ? (
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {tour.expected_duration_min} min
          </span>
        ) : null}
      </div>
      {tour.tour_type === "on_site" && (
        <p className="mt-2 text-xs text-muted-foreground">{availability.detail}</p>
      )}
      {tour.tour_type === "on_site" && tour.advisory_note && (
        <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
          {tour.advisory_note}
        </p>
      )}



      <div className="mt-auto flex flex-wrap gap-2 pt-4">
        <Button asChild size="sm" className="flex-1">
          <Link to="/guard-tours/$tourId" params={{ tourId: tour.id }}>
            <Play className="h-4 w-4" /> Open
          </Link>
        </Button>
        {canStart ? (
          <Button asChild size="sm" variant="outline">
            <Link to="/guard-tours/scan"><ScanLine className="h-4 w-4" /> Quick start</Link>
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled title={availability.detail}>
            <ScanLine className="h-4 w-4" /> Quick start
          </Button>
        )}
      </div>
    </Card>
  );
}
